import { generateTerminalKeyPair, licenceStatus, webCryptoSigner, type LicenceInfo, type LicenceStatus, type Signer, type TerminalIdentity, type TerminalSeries, type ModuleKey, type HomeScreen } from '@makila/pos-core';
import type { IdbTerminalStore } from './idb-store';

/** O que o terminal sabe de si próprio depois de activado (retrato para imprimir offline). */
export interface DeviceState {
  terminal: { id: string; code: string; name: string; keyId: string };
  store: { id: string; code: string; name: string; address: string };
  company: { id: string; legalName: string; tradeName: string | null; nuit: string; vatRegime: string; address: string; city: string; businessType: string; trainingMode: boolean; scaleBarcodeValue?: 'weight' | 'price'; loyaltyEnabled?: boolean;
    // Perfil de negócio (docs/14): ausentes num retrato antigo (antes da actualização do servidor).
    modules?: ModuleKey[]; home?: HomeScreen; phone?: string | null; email?: string | null; logo?: string | null; receiptFooter?: string | null };
  softwareName: string;
  deviceSecret: string;
  certificate: { keyId: string; tenantId: string; terminalId: string; publicKeyPem: string; issuedAt: string; signature: string };
  producerPublicKeyPem: string;
  licencePublicKeyPem: string;
  activatedAt: string;
}

export const API = '/api';

export class ApiError extends Error {
  constructor(readonly code: string, message: string, readonly status: number) { super(message); }
}

async function api<T>(path: string, init: RequestInit = {}, secret?: string): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API}${path}`, { ...init, headers: { ...(init.body !== undefined ? { 'content-type': 'application/json' } : {}), ...(secret ? { authorization: `Terminal ${secret}` } : {}), ...init.headers } });
  } catch {
    throw new ApiError('offline', 'Sem ligação à internet.', 0);
  }
  const body = await res.json().catch(() => null) as (T & { code?: string; message?: string }) | null;
  if (!res.ok || !body) throw new ApiError(body?.code ?? `http_${res.status}`, body?.message ?? `Erro ${res.status}`, res.status);
  return body;
}

interface SnapshotResponse {
  terminal: DeviceState['terminal']; store: DeviceState['store']; company: DeviceState['company']; softwareName: string;
  series: { id: string; doc_type: TerminalSeries['docType']; year: number; prefix: string; training: boolean; key_id: string; next_number: number; last_hash: string; last_issue_date: string | null; last_system_entry: string | null }[];
  licence: LicenceInfo;
}

const toSeries = (s: SnapshotResponse['series'][number]): TerminalSeries => ({
  seriesId: s.id, docType: s.doc_type, year: s.year, prefix: s.prefix, training: s.training, keyId: s.key_id,
  nextNumber: s.next_number, lastHash: s.last_hash, lastIssueDate: s.last_issue_date, lastSystemEntry: s.last_system_entry,
});

/**
 * Activação (uma vez, com internet): gera a chave do terminal — privada
 * não-exportável, nem este código a consegue ler — e troca o código do
 * backoffice pelo segredo, o certificado, as séries e a licença.
 */
export async function activate(store: IdbTerminalStore, activationCode: string): Promise<DeviceState> {
  const kp = await generateTerminalKeyPair();
  const r = await api<SnapshotResponse & Omit<DeviceState, 'terminal' | 'store' | 'company' | 'softwareName' | 'activatedAt'>>('/terminals/activate', {
    method: 'POST', body: JSON.stringify({ activationCode, publicKeyPem: kp.publicKeyPem }),
  });
  const device: DeviceState = {
    terminal: r.terminal, store: r.store, company: r.company, softwareName: r.softwareName,
    deviceSecret: r.deviceSecret, certificate: r.certificate, producerPublicKeyPem: r.producerPublicKeyPem,
    licencePublicKeyPem: r.licencePublicKeyPem, activatedAt: new Date().toISOString(),
  };
  await store.setKv('privateKey', kp.privateKey);
  await store.seed(r.series.map(toSeries));
  await store.saveLicence(r.licence);
  await store.setKv('device', device); // por último: só fica "activado" com tudo o resto gravado
  return device;
}

export async function loadDevice(store: IdbTerminalStore): Promise<{ device: DeviceState; signer: Signer } | null> {
  const device = await store.kv<DeviceState>('device');
  const key = await store.kv<CryptoKey>('privateKey');
  if (!device || !key) return null;
  return { device, signer: webCryptoSigner(device.terminal.keyId, key) };
}

/** Retrato actualizado (empresa, modo de formação, séries novas) e licença renovada. */
export async function refreshDevice(store: IdbTerminalStore, device: DeviceState): Promise<DeviceState> {
  const r = await api<SnapshotResponse>('/terminals/me', {}, device.deviceSecret);
  await store.saveLicence(r.licence);
  // Só séries que o terminal ainda não tem: o estado local vai à frente do da nuvem.
  const missing = r.series.map(toSeries).filter((s) => !store.series(s.docType, s.year, s.training));
  if (missing.length) await store.seed(missing);
  const next = { ...device, terminal: r.terminal, store: r.store, company: r.company, softwareName: r.softwareName };
  await store.setKv('device', next);
  return next;
}

export async function currentLicence(store: IdbTerminalStore, device: DeviceState): Promise<LicenceStatus> {
  const l = await store.kv<LicenceInfo>('licence');
  return licenceStatus(l?.token ?? null, device.licencePublicKeyPem, device.terminal.id);
}

export function identity(device: DeviceState): TerminalIdentity {
  return { terminalId: device.terminal.id, storeCode: device.store.code, terminalCode: device.terminal.code, keyId: device.terminal.keyId, training: device.company.trainingMode };
}

/** Documento de outra caixa, para uma devolução (precisa de rede). */
export async function lookupDocument(device: DeviceState, fullNumber: string): Promise<{ document: import('@makila/pos-core').IssuedDocument; alreadyCredited: Record<number, string> }> {
  return api(`/sync/documents?number=${encodeURIComponent(fullNumber)}`, {}, device.deviceSecret);
}

/**
 * Módulos activos na caixa. Num retrato antigo (sem `modules`), tudo como
 * antes: nada desaparece até o servidor mandar o perfil.
 */
export function hasModule(d: DeviceState, m: ModuleKey): boolean {
  return !d.company.modules || d.company.modules.includes(m);
}

/** Ecrã inicial da caixa: o do perfil, ou (retrato antigo) mesas no restaurante. */
export function homeOf(d: DeviceState): HomeScreen {
  return d.company.home ?? (d.company.businessType === 'restaurant' ? 'tables' : 'sale');
}

/** Pedido à nuvem em nome deste terminal (agenda e outras rotas com rede). */
export function terminalApi<T>(d: DeviceState, path: string, init: RequestInit = {}): Promise<T> {
  return api<T>(path, init, d.deviceSecret);
}
